import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { format, parseISO } from 'date-fns';
import { Branding } from '@/shared/hooks/useBranding';

export type InvoicePaperSize = 'A4' | 'A5' | 'THERMAL_80MM';

interface Props {
  inv:          any;
  branding:     Branding;
  hospitalName: string;
  paperSize:    InvoicePaperSize;
  onDone:       () => void;
}

const PAGE_CSS: Record<InvoicePaperSize, string> = {
  A4:           '@page { size: A4 portrait; margin: 12mm; }',
  A5:           '@page { size: A5 portrait; margin: 8mm; }',
  THERMAL_80MM: '@page { size: 80mm auto; margin: 0mm; }',
};

const BILLING_TYPE_LABEL: Record<string, string> = {
  cash: 'Cash', tpa: 'TPA', pmjay: 'PM-JAY',
  cghs: 'CGHS', echs: 'ECHS', esi: 'ESI',
};

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  paid:           { bg: '#dcfce7', color: '#166534' },
  cancelled:      { bg: '#fee2e2', color: '#991b1b' },
  draft:          { bg: '#f3f4f6', color: '#374151' },
  final:          { bg: '#dbeafe', color: '#1e40af' },
  partially_paid: { bg: '#fef9c3', color: '#854d0e' },
};

function rupees(n: any) {
  return '₹' + Number(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

export default function PrintableInvoice({ inv, branding, hospitalName, paperSize, onDone }: Props) {
  const [imgReady, setImgReady] = useState(false);
  const isThermal = paperSize === 'THERMAL_80MM';
  const isA5      = paperSize === 'A5';
  const headerUrl = branding.print_header_invoice;

  // ── Portal target ──────────────────────────────────────────────────
  const portalRef = useRef<HTMLDivElement | null>(null);
  if (!portalRef.current) {
    let el = document.getElementById('as-print-portal') as HTMLDivElement | null;
    if (!el) {
      el = document.createElement('div');
      el.id = 'as-print-portal';
      document.body.appendChild(el);
    }
    portalRef.current = el;
  }

  // No custom image — mark ready immediately
  useEffect(() => {
    if (!headerUrl) setImgReady(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When image is loaded (or no image), inject @page CSS and trigger print
  useEffect(() => {
    if (!imgReady) return;
    const style = document.createElement('style');
    style.id = 'as-inv-page';
    style.textContent = PAGE_CSS[paperSize];
    document.head.appendChild(style);
    const frame = requestAnimationFrame(() => {
      window.print();
      onDone();
    });
    return () => {
      cancelAnimationFrame(frame);
      style.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgReady]);

  const invoiceDate = inv.invoice_date
    ? format(parseISO(inv.invoice_date), 'dd MMM yyyy')
    : format(new Date(), 'dd MMM yyyy');

  const statusStyle = STATUS_COLORS[inv.status] ?? STATUS_COLORS.draft;
  const lineItems: any[] = inv.line_items ?? [];

  // ── Header block — handles image vs text fallback ──────────────────
  const headerBlock = headerUrl ? (
    <div style={{ width: '100%', marginBottom: isThermal ? '3mm' : '5mm' }}>
      <img
        src={headerUrl}
        alt="invoice header"
        onLoad={() => setImgReady(true)}
        onError={() => setImgReady(true)}
        style={{
          display:         'block',
          width:           '100%',
          maxHeight:       isThermal ? '20mm' : '40mm',
          objectFit:       'contain',
          objectPosition:  'left center',
        }}
      />
    </div>
  ) : (
    <div style={{
      display:       'flex',
      alignItems:    'flex-start',
      gap:           '10px',
      paddingBottom: '4mm',
      borderBottom:  '2px solid #1a1a2e',
      marginBottom:  isThermal ? '3mm' : '5mm',
      width:         '100%',
    }}>
      {branding.branding_logo ? (
        <img
          src={branding.branding_logo}
          alt="logo"
          style={{ width: '12mm', height: '12mm', objectFit: 'contain', flexShrink: 0, display: 'block' }}
        />
      ) : (
        <div style={{
          width:          '12mm',
          height:         '12mm',
          background:     '#1a1a2e',
          borderRadius:   '3mm',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          color:          '#fff',
          fontSize:       '9pt',
          fontWeight:     700,
          flexShrink:     0,
        }}>
          {hospitalName.trim().slice(0, 1).toUpperCase()}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: isThermal ? '10pt' : '13pt', lineHeight: 1.2, color: '#111' }}>
          {hospitalName}
        </div>
        {branding.branding_tagline && (
          <div style={{ fontSize: '7pt', color: '#555', marginTop: '1px' }}>
            {branding.branding_tagline}
          </div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '2px', fontSize: '7pt', color: '#444' }}>
          {branding.hospital_address && <span>{branding.hospital_address}</span>}
          {branding.hospital_phone   && <span>&#9990;&nbsp;{branding.hospital_phone}</span>}
          {branding.hospital_email   && <span>&#9993;&nbsp;{branding.hospital_email}</span>}
        </div>
      </div>
    </div>
  );

  // ── THERMAL LAYOUT (80mm roll — no table, monospace stacked) ──────
  if (isThermal) {
    return createPortal(
      <div style={{
        width:           '80mm',
        fontFamily:      'monospace',
        fontSize:        '8pt',
        color:           '#111',
        padding:         '3mm 3mm',
        boxSizing:       'border-box',
        backgroundColor: '#fff',
      }}>
        {headerBlock}

        <div style={{ textAlign: 'center', marginBottom: '2mm' }}>
          <div style={{ fontWeight: 700, fontSize: '10pt' }}>TAX INVOICE</div>
          <div style={{ fontSize: '7pt', color: '#555' }}>{inv.invoice_number}</div>
          <div style={{ fontSize: '7pt', color: '#555' }}>{invoiceDate}</div>
        </div>

        <div style={{ borderTop: '1px dashed #aaa', margin: '2mm 0' }}/>

        <div style={{ marginBottom: '2mm' }}>
          <div style={{ fontWeight: 700 }}>{inv.patient_name ?? inv.patient_uid}</div>
          <div style={{ fontSize: '7pt', color: '#555' }}>
            {[inv.patient_uid, inv.gender, inv.age_years ? inv.age_years + ' yrs' : null].filter(Boolean).join(' · ')}
          </div>
        </div>

        <div style={{ borderTop: '1px dashed #aaa', margin: '2mm 0' }}/>

        {lineItems.map((l: any, i: number) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5mm', gap: '4px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ wordBreak: 'break-word' }}>{l.description}</div>
              {Number(l.quantity) !== 1 && (
                <div style={{ fontSize: '7pt', color: '#777' }}>
                  x{l.quantity} @ {rupees(l.unit_rate)}
                </div>
              )}
            </div>
            <div style={{ flexShrink: 0, fontWeight: 600 }}>{rupees(l.line_total)}</div>
          </div>
        ))}

        <div style={{ borderTop: '1px dashed #aaa', margin: '2mm 0' }}/>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1mm' }}>
          <span>Subtotal</span><span>{rupees(inv.subtotal)}</span>
        </div>
        {Number(inv.discount_amount) > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1mm', color: '#dc2626' }}>
            <span>Discount</span><span>-{rupees(inv.discount_amount)}</span>
          </div>
        )}
        {Number(inv.cgst_amount) > 0 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1mm' }}>
              <span>CGST</span><span>{rupees(inv.cgst_amount)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1mm' }}>
              <span>SGST</span><span>{rupees(inv.sgst_amount)}</span>
            </div>
          </>
        )}

        <div style={{
          borderTop:      '2px solid #111',
          margin:         '2mm 0',
          paddingTop:     '1.5mm',
          display:        'flex',
          justifyContent: 'space-between',
          fontWeight:     700,
          fontSize:       '10pt',
        }}>
          <span>TOTAL</span><span>{rupees(inv.total_amount)}</span>
        </div>

        {Number(inv.amount_paid) > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1mm', color: '#16a34a' }}>
            <span>Paid</span><span>{rupees(inv.amount_paid)}</span>
          </div>
        )}
        {Number(inv.amount_due) > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#dc2626', fontWeight: 700 }}>
            <span>Balance Due</span><span>{rupees(inv.amount_due)}</span>
          </div>
        )}

        {inv.notes && (
          <div style={{ marginTop: '2mm', fontSize: '7pt', color: '#555', borderTop: '1px dashed #aaa', paddingTop: '2mm' }}>
            Notes: {inv.notes}
          </div>
        )}
        {branding.branding_invoice_footer && (
          <div style={{ marginTop: '2mm', fontSize: '7pt', color: '#555', textAlign: 'center' }}>
            {branding.branding_invoice_footer}
          </div>
        )}
        <div style={{ marginTop: '3mm', borderTop: '1px dashed #aaa', paddingTop: '2mm', textAlign: 'center', fontSize: '6.5pt', color: '#777' }}>
          Thank you for choosing {hospitalName}
        </div>
      </div>,
      portalRef.current
    );
  }

  // ── A4 / A5 TABLE LAYOUT ──────────────────────────────────────────
  const baseFontSize  = isA5 ? '10pt' : '11pt';
  const smallFontSize = isA5 ? '8pt'  : '9pt';
  const headFontSize  = isA5 ? '8.5pt': '10pt';
  const cellPad       = isA5 ? '5px 8px' : '7px 10px';

  return createPortal(
    <div style={{
      fontFamily:      'Arial, Helvetica, sans-serif',
      fontSize:        baseFontSize,
      color:           '#111',
      backgroundColor: '#fff',
      boxSizing:       'border-box',
    }}>
      {headerBlock}

      {/* Invoice title + status */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isA5 ? '4mm' : '5mm' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: isA5 ? '15pt' : '18pt', color: '#1a1a2e', lineHeight: 1.1 }}>
            Tax Invoice
          </div>
          <div style={{ fontSize: smallFontSize, color: '#555', marginTop: '2px' }}>
            {inv.invoice_number}&nbsp;&middot;&nbsp;{invoiceDate}
          </div>
          {inv.billing_type && (
            <div style={{ fontSize: smallFontSize, color: '#555' }}>
              {BILLING_TYPE_LABEL[inv.billing_type] ?? inv.billing_type}
            </div>
          )}
        </div>
        <div style={{
          display:      'inline-block',
          padding:      '3px 12px',
          borderRadius: '20px',
          fontSize:     smallFontSize,
          fontWeight:   700,
          background:   statusStyle.bg,
          color:        statusStyle.color,
          flexShrink:   0,
        }}>
          {inv.status.replace(/_/g, ' ').toUpperCase()}
        </div>
      </div>

      {/* Patient info */}
      <div style={{
        background:    '#f8f9fa',
        borderRadius:  '6px',
        padding:       cellPad,
        marginBottom:  isA5 ? '4mm' : '5mm',
        fontSize:      smallFontSize,
        lineHeight:    1.7,
      }}>
        <strong>{inv.patient_name ?? inv.patient_uid}</strong>&nbsp;&middot;&nbsp;{inv.patient_uid}
        <br/>
        {[inv.gender, inv.age_years ? inv.age_years + ' yrs' : null, inv.patient_mobile].filter(Boolean).join(' · ')}
      </div>

      {/* Line items table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: isA5 ? '4mm' : '5mm' }}>
        <thead>
          <tr>
            {[
              { label: 'Description', align: 'left'  as const },
              { label: 'Category',    align: 'center' as const },
              { label: 'Qty',         align: 'right'  as const },
              { label: 'Rate',        align: 'right'  as const },
              { label: 'Disc',        align: 'right'  as const },
              { label: 'GST',         align: 'right'  as const },
              { label: 'Amount',      align: 'right'  as const },
            ].map(({ label, align }) => (
              <th key={label} style={{
                background:  '#1a1a2e',
                color:       '#fff',
                padding:     cellPad,
                fontSize:    headFontSize,
                textAlign:   align,
                fontWeight:  700,
                whiteSpace:  'nowrap',
              }}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lineItems.map((l: any, idx: number) => (
            <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
              <td style={{ padding: cellPad, borderBottom: '1px solid #eee' }}>{l.description}</td>
              <td style={{ padding: cellPad, borderBottom: '1px solid #eee', textAlign: 'center' }}>{l.category}</td>
              <td style={{ padding: cellPad, borderBottom: '1px solid #eee', textAlign: 'right' }}>{l.quantity}</td>
              <td style={{ padding: cellPad, borderBottom: '1px solid #eee', textAlign: 'right' }}>{rupees(l.unit_rate)}</td>
              <td style={{ padding: cellPad, borderBottom: '1px solid #eee', textAlign: 'right' }}>
                {Number(l.discount_pct) > 0 ? l.discount_pct + '%' : '—'}
              </td>
              <td style={{ padding: cellPad, borderBottom: '1px solid #eee', textAlign: 'right' }}>
                {Number(l.gst_rate) > 0 ? l.gst_rate + '%' : 'Exempt'}
              </td>
              <td style={{ padding: cellPad, borderBottom: '1px solid #eee', textAlign: 'right', fontWeight: 600 }}>
                {rupees(l.line_total)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <table style={{ marginLeft: 'auto', width: isA5 ? '190px' : '250px', borderCollapse: 'collapse', marginBottom: isA5 ? '4mm' : '5mm' }}>
        <tbody>
          <tr>
            <td style={{ padding: '3px 8px', fontSize: smallFontSize }}>Subtotal</td>
            <td style={{ padding: '3px 8px', fontSize: smallFontSize, textAlign: 'right' }}>{rupees(inv.subtotal)}</td>
          </tr>
          {Number(inv.discount_amount) > 0 && (
            <tr>
              <td style={{ padding: '3px 8px', fontSize: smallFontSize, color: '#dc2626' }}>Discount</td>
              <td style={{ padding: '3px 8px', fontSize: smallFontSize, textAlign: 'right', color: '#dc2626' }}>-{rupees(inv.discount_amount)}</td>
            </tr>
          )}
          {Number(inv.cgst_amount) > 0 && (
            <>
              <tr>
                <td style={{ padding: '3px 8px', fontSize: smallFontSize }}>CGST</td>
                <td style={{ padding: '3px 8px', fontSize: smallFontSize, textAlign: 'right' }}>{rupees(inv.cgst_amount)}</td>
              </tr>
              <tr>
                <td style={{ padding: '3px 8px', fontSize: smallFontSize }}>SGST</td>
                <td style={{ padding: '3px 8px', fontSize: smallFontSize, textAlign: 'right' }}>{rupees(inv.sgst_amount)}</td>
              </tr>
            </>
          )}
          <tr>
            <td style={{ padding: '5px 8px', fontSize: baseFontSize, fontWeight: 700, borderTop: '2px solid #1a1a2e' }}>
              Total
            </td>
            <td style={{ padding: '5px 8px', fontSize: baseFontSize, fontWeight: 700, textAlign: 'right', borderTop: '2px solid #1a1a2e' }}>
              {rupees(inv.total_amount)}
            </td>
          </tr>
          {Number(inv.amount_paid) > 0 && (
            <tr>
              <td style={{ padding: '3px 8px', fontSize: smallFontSize, color: '#16a34a' }}>Paid</td>
              <td style={{ padding: '3px 8px', fontSize: smallFontSize, textAlign: 'right', color: '#16a34a' }}>{rupees(inv.amount_paid)}</td>
            </tr>
          )}
          {Number(inv.amount_due) > 0 && (
            <tr>
              <td style={{ padding: '3px 8px', fontSize: smallFontSize, color: '#dc2626', fontWeight: 700 }}>Balance Due</td>
              <td style={{ padding: '3px 8px', fontSize: smallFontSize, textAlign: 'right', color: '#dc2626', fontWeight: 700 }}>
                {rupees(inv.amount_due)}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {inv.notes && (
        <p style={{ color: '#555', fontSize: smallFontSize, marginBottom: '4mm' }}>
          Notes: {inv.notes}
        </p>
      )}

      {branding.branding_invoice_footer && (
        <div style={{ borderTop: '1px solid #d1d5db', paddingTop: '3mm', marginTop: '3mm', fontSize: smallFontSize, color: '#555' }}>
          {branding.branding_invoice_footer}
        </div>
      )}
    </div>,
    portalRef.current
  );
}
